import { useState, useEffect, useCallback } from 'react';
// Note: Link is not used in this new design, but kept in imports if you want to add it back.
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useAuth } from '../../contexts/AuthContext';
import apiWithAuth from '../../services/apiWithAuth';
import { Link } from 'react-router-dom';

// SVG Icons for better UI
const MailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);

const PhoneIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);


const Profile = () => {
    // --- DATA FETCHING LOGIC (UNCHANGED) ---
    const [employeeData, setEmployeeData] = useState(() => {
        try {
            const cached = localStorage.getItem('kd_admin_employee_profile');
            return cached ? JSON.parse(cached) : null;
        } catch (_) {
            return null;
        }
    });

    const { loading, error, makeRequest } = useAuthenticatedApi();
    const { user } = useAuth();

    const fetchEmployeeData = useCallback(async () => {
        if (!user || !user.id) return;
        const response = await makeRequest(async () => {
            const url = `/employees/${user.id}`;
            return await apiWithAuth.get(url);
        });

        if (response) {
            const employee = response.data.data || response.data;
            setEmployeeData(employee);
            try {
                localStorage.setItem('kd_admin_employee_profile', JSON.stringify(employee));
                window.dispatchEvent(new CustomEvent('kd_admin_employee_profile_updated', { detail: employee }));
            } catch (_) { }
        }
    }, [makeRequest, user]);

    useEffect(() => {
        if (user && user.id) {
            fetchEmployeeData();
        }
    }, [user, fetchEmployeeData]);

    useEffect(() => {
        if (!user || !user.id) return;
        const intervalId = setInterval(() => {
            fetchEmployeeData();
        }, 5 * 60 * 1000); // 5 minutes
        return () => clearInterval(intervalId);
    }, [user, fetchEmployeeData]);

    // --- DERIVED STATE & CONSTANTS (UNCHANGED) ---
    const defaultAvatar = '/default-avatar.png';
    const fullName = employeeData ? `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() : 'Unknown User';
    const profileAvatar = employeeData && (employeeData.profileImageUrl || employeeData.avatarUrl || employeeData.profileImage || employeeData.imageUrl || employeeData.avatar);
    const avatarUrl = (profileAvatar && !String(profileAvatar).includes('png.pngtree.com')) ? profileAvatar : defaultAvatar;

    // --- LOADING AND ERROR STATES (UNCHANGED) ---
    if (loading && !employeeData) {
        return <div className="flex justify-center items-center h-64 text-lg text-gray-600">Loading profile...</div>;
    }

    if (!employeeData) {
        return <div className="flex justify-center items-center h-64 text-lg text-gray-600">No profile data available</div>;
    }

    // --- NEW REDESIGNED JSX ---
    return (
        <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8 flex items-center justify-center">
            <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4">

                    {/* Left Sidebar */}
                    <aside className="md:col-span-1 lg:col-span-1 p-6 md:border-r md:border-gray-200">
                        <div className="flex flex-col items-center text-center">
                            {/* ... (Existing Profile Image, Name, Position, Status Badge) */}
                            <img
                                src={avatarUrl}
                                alt={fullName}
                                className="w-32 h-32 rounded-full object-cover ring-4 ring-gray-200"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = defaultAvatar;
                                }}
                            />
                            <h1 className="mt-4 text-2xl font-bold text-gray-900">{fullName}</h1>
                            <p className="mt-1 text-gray-600">{employeeData.position ?? employeeData.designation ?? employeeData.role?.name ?? 'Employee'}</p>

                            <div className="mt-4">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${employeeData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {employeeData.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>

                        <hr className="my-6 border-gray-200" />

                        {/* 🌟 NEW ATTENDANCE BUTTON 🌟 */}
                        {/* Assuming a route like '/my-attendance' or similar for the Link */}
                        <Link
                            to="/my-attendance" // <-- Adjust this path based on your router setup
                            className="flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <CalendarIcon />
                            View/Mark Attendance
                        </Link>

                        <hr className="my-6 border-gray-200" /> {/* Added separator for cleanliness */}

                        <div className="space-y-4 text-gray-700">
                            {/* ... (Existing Mail and Phone details) */}
                            <div className="flex items-center space-x-3">
                                <MailIcon />
                                <span className="text-sm truncate">{employeeData.email || 'No email provided'}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <PhoneIcon />
                                <span className="text-sm">{employeeData.phone || 'No phone provided'}</span>
                            </div>
                        </div>
                    </aside>

                    {/* Right Main Content */}
                    {/* ... (Existing main content remains the same) */}
                    <main className="md:col-span-2 lg:col-span-3 p-6 sm:p-8">
                        {error && (
                            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                                {error}
                            </div>
                        )}

                        {/* Section: Profile Details */}
                        <section>
                            <h2 className="text-xl font-semibold text-gray-800">Profile Details</h2>
                            <hr className="mt-2 mb-6 border-gray-200" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">First Name</label>
                                    <p className="mt-1 text-gray-900">{employeeData.firstName || '—'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Last Name</label>
                                    <p className="mt-1 text-gray-900">{employeeData.lastName || '—'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Email Address</label>
                                    <p className="mt-1 text-gray-900">{employeeData.email || '—'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                                    <p className="mt-1 text-gray-900">{employeeData.phone || '—'}</p>
                                </div>
                            </div>
                        </section>

                        {/* Section: Employment Information */}
                        <section className="mt-10">
                            <h2 className="text-xl font-semibold text-gray-800">Employment Information</h2>
                            <hr className="mt-2 mb-6 border-gray-200" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Employee ID</label>
                                    <p className="mt-1 text-gray-900">{employeeData.employeeId || employeeData._id || 'Not available'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Department</label>
                                    <p className="mt-1 text-gray-900">{employeeData.department ?? employeeData.dept ?? 'Not provided'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Position</label>
                                    <p className="mt-1 text-gray-900">{employeeData.position ?? employeeData.designation ?? employeeData.role?.name ?? 'Not provided'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Hire Date</label>
                                    <p className="mt-1 text-gray-900">
                                        {employeeData.hireDate ? new Date(employeeData.hireDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Member Since</label>
                                    <p className="mt-1 text-gray-900">
                                        {employeeData.createdAt ? new Date(employeeData.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                                    <p className="mt-1 text-gray-900">
                                        {employeeData.updatedAt ? new Date(employeeData.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                                    </p>
                                </div>
                            </div>
                        </section>
                    </main>

                </div>
            </div>
        </div>
    );
};

export default Profile;